/**
 * Solver driver (Faz 6.3/6.4): turns the ConstraintSet into particle
 * relaxation problems and writes results back through the store.
 *
 * Mirrors the OCS separation — constraints become parameter algebra over
 * ref points, independent subsystems are split along shared entity
 * components, each subsystem solves with the GeometricConstraintSolver
 * relaxation core (max 50 iterations, convergence < 1e-6 * scale) and
 * unsatisfiable subsystems land in the store's conflict cache. Silent
 * updates follow the dimAssociation pattern: updateEntity writes are
 * history-free (recompute is never a user command).
 */

import { useAppStore, type Entity } from '../../state/store';
import { GeometricConstraintSolver, type ConstraintType } from '../GeometricConstraintSolver';
import { useConstraintStore } from './constraintStore';
import { applyMarkerPoints, particleMarkers, resolveMarkerPoint } from './attach';
import { evaluateExpression, ParamError } from './parameters';
import { isDimensionalKind } from './kinds';
import { MARKER_CENTER, MARKER_RADIUS } from './types';
import type { ConstraintRef, ConstraintSet, DrivingValue } from './types';
import type { Pt3 } from '../commands/geometryMath';

export interface SolveResult {
    converged: boolean;
    iterations: number;
    residual: number;
    /** Constraint ids that failed to converge. */
    conflicts: string[];
}

/** Current driver behavior context for one solve pass. */
export interface SolveOptions {
    /** Refs held as pinned anchors (grip commit: the dragged grip drives). */
    anchorRefs?: ConstraintRef[];
    /** Skip the store conflict/value write-back (dry-run measurement). */
    silent?: boolean;
}

interface BuiltConstraint {
    id: string;
    type: ConstraintType;
    particles: string[];
    values: number[];
}

const particleIdOf = (ref: ConstraintRef): string => `${ref.entityId}:${ref.marker}`;

/** Resolve a driving value to a number (literal or parameter expression). */
export function resolveDriving(driving: DrivingValue | undefined, set: ConstraintSet, fallback: number): number {
    if (!driving) return fallback;
    if ('literal' in driving) return driving.literal;
    try {
        return evaluateExpression(driving.named, set.parameters);
    } catch (error) {
        if (error instanceof ParamError) return fallback;
        throw error;
    }
}

/**
 * Flatten one constraint record into solver particle refs + values.
 * Returns null for kinds the driver cannot express (Smooth is a recorded
 * stub; disabled/degenerate refs are skipped by the caller).
 */
function buildConstraint(record: ConstraintSet['constraints'][number], set: ConstraintSet): BuiltConstraint | null {
    const r = record.refs.map(particleIdOf);
    switch (record.kind) {
        case 'Coincident':
            return r.length >= 2 ? { id: record.id, type: 'Coincident', particles: [r[0], r[1]], values: [] } : null;
        case 'Concentric':
            return r.length >= 2 ? { id: record.id, type: 'Concentric', particles: [r[0], r[1]], values: [] } : null;
        case 'Horizontal':
        case 'Vertical': {
            // One line entity, or one endpoint pair of a polyline segment.
            if (r.length >= 2) return { id: record.id, type: record.kind, particles: [r[0], r[1]], values: [] };
            return null;
        }
        case 'Parallel':
        case 'Perpendicular':
            return r.length >= 4 ? { id: record.id, type: record.kind, particles: r.slice(0, 4), values: [] } : null;
        case 'Angle': {
            if (r.length < 4) return null;
            const rad = (resolveDriving(record.driving, set, record.value ?? 0) * Math.PI) / 180;
            return { id: record.id, type: 'Angle', particles: r.slice(0, 4), values: [rad] };
        }
        case 'Tangent': {
            if (r.length < 4) return null;
            // Circle-circle encodings are tagged by the command layer.
            return { id: record.id, type: 'Tangent', particles: r.slice(0, 4), values: [0, record.value === -1 ? 1 : 0] };
        }
        case 'Equal': {
            if (r.length < 4) return null;
            const circleMode = record.value === -1 ? 1 : 0;
            return { id: record.id, type: 'Equality', particles: r.slice(0, 4), values: [circleMode] };
        }
        case 'Midpoint':
            return r.length >= 3 ? { id: record.id, type: 'Midpoint', particles: r.slice(0, 3), values: [] } : null;
        case 'PointOnCurve': {
            if (r.length < 3) return null;
            const circular = record.value === -1 ? 1 : 0;
            return { id: record.id, type: 'PointOnCurve', particles: r.slice(0, 3), values: [circular] };
        }
        case 'Colinear':
            return r.length >= 4 ? { id: record.id, type: 'Colinear', particles: r.slice(0, 4), values: [] } : null;
        case 'Symmetric':
            return r.length >= 4 ? { id: record.id, type: 'Symmetric', particles: r.slice(0, 4), values: [] } : null;
        case 'Distance': {
            // Two refs = point pair; one ref = line length (line) or
            // diameter (circle) per the OCS ref-shape derivation.
            if (r.length >= 2) {
                const val = resolveDriving(record.driving, set, record.value ?? 0);
                return { id: record.id, type: 'Distance', particles: [r[0], r[1]], values: [val] };
            }
            return null;
        }
        case 'DistanceX':
        case 'DistanceY': {
            if (r.length >= 2) {
                const val = resolveDriving(record.driving, set, record.value ?? 0);
                return { id: record.id, type: record.kind, particles: [r[0], r[1]], values: [val] };
            }
            return null;
        }
        case 'Radius':
        case 'Diameter': {
            if (r.length >= 2) {
                const val = resolveDriving(record.driving, set, record.value ?? 0);
                const radius = record.kind === 'Diameter' ? Math.abs(val) / 2 : Math.abs(val);
                return { id: record.id, type: 'Distance', particles: [r[0], r[1]], values: [radius] };
            }
            return null;
        }
        default:
            return null;
    }
}

/**
 * Solve the document's constraint set. Returns the solve report; on
 * convergence the solved marker positions are written back silently and
 * bound dimensions are re-synced to the fresh geometry. Re-entrant calls
 * (the watcher firing during this solve's own silent write-backs) drop out
 * with a no-op so a solve pass is always atomic.
 */
let solvingNow = false;

export function solveConstraintSet(options: SolveOptions = {}): SolveResult {
    if (solvingNow) return { converged: true, iterations: 0, residual: 0, conflicts: [] };
    solvingNow = true;
    try {
        return solveConstraintSetInner(options);
    } finally {
        solvingNow = false;
    }
}

function solveConstraintSetInner(options: SolveOptions = {}): SolveResult {
    const store = useConstraintStore.getState();
    const set = store.set;
    const entities = useAppStore.getState().entities;

    const result: SolveResult = { converged: true, iterations: 0, residual: 0, conflicts: [] };
    if (set.constraints.length === 0) return result;

    // Dimensional values to refresh into the store after the solve (the
    // resolved driving values — literal or parameter expression).
    const values: Record<string, number> = {};

    // --- Collect particles (entity+marker pairs) and enabled constraints ---
    const particleEntities = new Map<string, string>(); // particleId -> entityId
    const particlePoints = new Map<string, Pt3>();
    const alive = new Set<string>();
    for (const entity of Object.values(entities)) {
        for (const marker of particleMarkers(entity)) {
            const p = resolveMarkerPoint(entity, marker);
            if (!p) continue;
            const pid = `${entity.id}:${marker}`;
            particleEntities.set(pid, entity.id);
            particlePoints.set(pid, p);
            alive.add(pid);
        }
    }

    const anchorSet = new Set((options.anchorRefs ?? []).map(particleIdOf));

    // Fixed constraints pin their refs (a Fixed point never becomes a driver
    // target — OCS: Fixed-held points cannot be grip drivers). The pinned
    // particles re-base onto their RECORDED world points, so moving a Fixed
    // object is reverted by the solve instead of becoming the new truth.
    const fixedParticles = new Set<string>();
    for (const c of set.constraints) {
        if (c.enabled && c.kind === 'Fixed') {
            c.refs.forEach((ref, i) => {
                const pid = particleIdOf(ref);
                fixedParticles.add(pid);
                const frozen = c.fixedPoints?.[i];
                if (frozen && Array.isArray(frozen) && frozen.length >= 2) {
                    particlePoints.set(pid, [frozen[0], frozen[1], frozen[2] ?? 0]);
                }
            });
        }
    }

    const built: BuiltConstraint[] = [];
    for (const record of set.constraints) {
        if (!record.enabled || record.kind === 'Fixed' || record.kind === 'Smooth') continue;
        // Skip constraints whose entity refs are missing (deferred cleanup).
        const refsAlive = record.refs.every((ref) => entities[ref.entityId]);
        if (!refsAlive) continue;
        // Refresh the resolved driving value for dimensional reporting and
        // the bound-dimension sync (parameter expressions may have changed).
        if (isDimensionalKind(record.kind)) {
            values[record.id] = resolveDriving(record.driving, set, record.value ?? 0);
        }
        const bc = buildConstraint(record, set);
        if (!bc) continue;
        if (!bc.particles.every((pid) => alive.has(pid))) continue;
        built.push(bc);
    }

    // --- Split into independent subsystems over shared entities ---
    const parent = new Map<string, string>();
    const find = (x: string): string => {
        let root = x;
        while (parent.get(root) !== root) root = parent.get(root)!;
        return root;
    };
    const union = (a: string, b: string): void => {
        const ra = find(a), rb = find(b);
        if (ra !== rb) parent.set(ra, rb);
    };
    for (const bc of built) {
        const [first] = bc.particles;
        for (const pid of bc.particles) {
            if (!parent.has(pid)) parent.set(pid, pid);
            union(first, pid);
        }
    }

    const subsystems = new Map<string, BuiltConstraint[]>();
    for (const bc of built) {
        const root = find(bc.particles[0]);
        const list = subsystems.get(root) ?? [];
        list.push(bc);
        subsystems.set(root, list);
    }

    // --- Solve each subsystem independently ---
    const conflicts: string[] = [];
    let iterations = 0;
    let convergedAll = true;

    subsystems.forEach((list) => {
        const solver = new GeometricConstraintSolver();
        const usedParticles = new Set<string>();
        list.forEach((bc) => bc.particles.forEach((pid) => usedParticles.add(pid)));

        usedParticles.forEach((pid) => {
            const p = particlePoints.get(pid)!;
            const pinned = fixedParticles.has(pid) || anchorSet.has(pid);
            solver.addPoint(pid, p[0], p[1], p[2] ?? 0, pinned);
        });
        // Rigid follow: every circle/arc radius handle trails its center's
        // translation (the handle still moves radially under Radius/
        // Diameter/Distance constraints — delta-based, not absolute).
        usedParticles.forEach((pid) => {
            const marker = Number(pid.slice(pid.lastIndexOf(':') + 1));
            if (marker === MARKER_RADIUS) {
                const centerPid = `${pid.slice(0, pid.lastIndexOf(':'))}:${MARKER_CENTER}`;
                if (usedParticles.has(centerPid)) solver.bindCompanion(pid, centerPid);
            }
        });
        list.forEach((bc) => {
            solver.addConstraintWithId(bc.id, bc.type, bc.particles, bc.values);
        });

        const report = solver.solveDetailed();
        iterations = Math.max(iterations, report.iterations);
        if (!report.converged) {
            convergedAll = false;
            list.forEach((bc) => conflicts.push(bc.id));
            return; // Leave the subsystem geometry untouched.
        }

        // Write solved positions back per entity (silent, history-free).
        const perEntity = new Map<string, Map<number, Pt3>>();
        solver.getPositions().forEach((pos, pid) => {
            const entityId = particleEntities.get(pid);
            if (!entityId) return;
            const entity = entities[entityId];
            if (!entity) return;
            const markerStr = pid.slice(entityId.length + 1);
            const marker = Number(markerStr);
            if (!Number.isFinite(marker)) return;
            if (!particleMarkers(entity).includes(marker)) return;
            const bag = perEntity.get(entityId) ?? new Map<number, Pt3>();
            const cur = resolveMarkerPoint(entity, marker);
            if (cur && Math.hypot(cur[0] - pos.x, cur[1] - pos.y) < 1e-12) return;
            bag.set(marker, [pos.x, pos.y, pos.z]);
            perEntity.set(entityId, bag);
        });

        perEntity.forEach((bag, entityId) => {
            const entity = useAppStore.getState().entities[entityId];
            if (!entity) return;
            const nextData = applyMarkerPoints(entity, bag);
            if (nextData) useAppStore.getState().updateEntity(entityId, { data: nextData });
        });
    });

    result.converged = convergedAll;
    result.iterations = iterations;
    result.conflicts = conflicts;
    if (!options.silent) {
        useConstraintStore.getState().applySolveResult(conflicts, values);
    }
    return result;
}

/**
 * Re-sync dimension entities bound to dimensional constraints
 * (OCS set.dimensions): the dimension's definition points follow the
 * solved geometry and its measured value mirrors the constraint value.
 */
export function syncBoundDimensions(): void {
    const { set } = useConstraintStore.getState();
    const entities = useAppStore.getState().entities;
    for (const [constraintId, dimId] of Object.entries(set.dimensions)) {
        const record = set.constraints.find((c) => c.id === constraintId);
        const dim = entities[dimId];
        if (!record || !dim || dim.type !== 'dimension') continue;
        const p1 = record.refs[0] ? resolveMarkerPoint(entities[record.refs[0].entityId], record.refs[0].marker) : null;
        const p2 = record.refs[1] ? resolveMarkerPoint(entities[record.refs[1].entityId], record.refs[1].marker) : null;
        const next: Record<string, any> = { ...dim.data, constraintDriven: constraintId };
        const value = record.value !== undefined ? Math.abs(record.value) : undefined;
        if (record.kind === 'Radius' || record.kind === 'Diameter') {
            const radius = record.kind === 'Diameter' ? (value ?? 0) / 2 : (value ?? 0);
            if (value !== undefined) { next.measured = value; next.radius = radius; }
            if (p1 && Array.isArray(dim.data.radiusPoint)) {
                // Radial dims keep their own text anchor: refresh the measured
                // radius point against the solved radius.
                const oldRp = dim.data.radiusPoint as number[];
                const dirX = (oldRp[0] - p1[0]) || 1;
                const dirY = oldRp[1] - p1[1];
                const len = Math.hypot(dirX, dirY) || 1;
                next.radiusPoint = [p1[0] + (dirX / len) * radius, p1[1] + (dirY / len) * radius, oldRp[2] ?? p1[2]];
            }
        } else if (record.kind === 'Angle') {
            if (value !== undefined) {
                next.angle = value;
                next.override = `${Number(value).toFixed(1)}°`;
            }
            if (p1) next.point1 = [...p1];
            if (p2) next.point2 = [...p2];
        } else {
            if (value !== undefined) next.measured = value;
            if (p1) next.point1 = [...p1];
            if (p2) next.point2 = [...p2];
        }
        useAppStore.getState().updateEntity(dimId, { data: next });
    }
}
